import { getChartExportWarning } from "../../common/export/exportWarnings.ts";
import ChartReportExport from "../../common/export/ChartReportExport.tsx";
import type { ExportChartFormats } from "../../viewer/chart/ChartProps.ts";
import { useRef } from "react";
import { Box, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { selectCurrentReportName, selectCurrentReportType } from "../../../store/selectors/currentReportSelectors";
import { selectChartSettingsState } from "../../../store/selectors/fieldsStateSelectors.ts";
import PartContainer from "../../viewer/common/PartContainer.tsx";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import EditingHistoryControls from "../common/EditingHistoryControls.tsx";
import type { EditorProps } from "../../viewer/common/Types.ts";
import BuildProgressWrapper from "../BuildProgressWrapper.tsx";
import ChangePreviewModeButton from "../common/ChangePreviewModeButton.tsx";
import BuilderMoreAction from "../common/fields/BuilderMoreAction.tsx";
import ChartContainer from "./ChartContainer.tsx";
import EditPreviewComponent from "./EditPreviewComponent.tsx";
import useChartStateBuilder from "./hooks/useChartStateBuilder.ts";
import useTrackMeasuresListUpdate from "./hooks/useTrackMeasuresListUpdate.ts";
import TableComponent from "./table/TableComponent.tsx";

export default function ChartEditor({ exportAllowed = true, ReportTypeSelector }: EditorProps) {
  useTrackMeasuresListUpdate();
  const { onRefreshGrid } = useBiCommonDataContext();

  return (
    <ChartContainer>
      <Stack direction="row" sx={{ flex: 1 }}>
        <PartContainer
          sx={{ maxWidth: "24rem", minWidth: "24rem" }}
          caption={<Box sx={{ pl: "1.3rem" }}>Chart Builder</Box>}
          actions={<BuilderMoreAction />}
        >
          <TableComponent ReportTypeSelector={ReportTypeSelector} />
        </PartContainer>
        <BuildProgressWrapper>
          <PartContainer
            sx={{ borderRight: "unset", p: "1.3rem" }}
            containerSx={{ position: "relative", gap: "1rem" }}
            caption="Preview"
            actions={
              <PreviewActions
                onRefresh={onRefreshGrid}
                ChangePreviewModeButton={<ChangePreviewModeButton />}
                EditingHistory={<EditingHistoryControls />}
              />
            }
          >
            <PreviewContainer exportAllowed={exportAllowed} />
          </PartContainer>
        </BuildProgressWrapper>
      </Stack>
    </ChartContainer>
  );
}

function PreviewContainer({ exportAllowed = true }: { exportAllowed?: boolean }) {
  const exportChartRef = useRef<((fileName: string, format: ExportChartFormats) => void) | undefined>(undefined);
  const state = useChartStateBuilder();
  const reportName = useSelector(selectCurrentReportName);
  const reportType = useSelector(selectCurrentReportType);
  const settings = useSelector(selectChartSettingsState);

  return (
    <>
      {exportAllowed && (
        <ChartReportExport
          reportName={reportName}
          warning={getChartExportWarning(state)}
          onExport={(fileName, format) => exportChartRef.current?.(fileName, format)}
        />
      )}
      <EditPreviewComponent
        state={state}
        reportType={reportType}
        settings={settings}
        exportFuncCallback={(callback) => {
          exportChartRef.current = callback;
        }}
      />
    </>
  );
}
